import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { supabase } from '@/src/core/config/supabase';
import {
  deletePostUseCase,
  followRepository,
  followUserUseCase,
  friendshipRepository,
  getSocialCountsUseCase,
  getSubscriptionUseCase,
  getUserPostsUseCase,
  getUserStatsUseCase,
  getUserTournamentsUseCase,
  getUserTrainingStatsUseCase,
  likePostUseCase,
  participantRepository,
  sendChallengeUseCase,
  sendNotificationUseCase,
  sendFriendRequestUseCase,
  unfollowUserUseCase,
  unlikePostUseCase,
} from '@/src/core/dependencies';
import { logger } from '@/src/core/utils';
import { PostWithDetails, SocialCounts, Tournament } from '@/src/domain/models';
import type { UserTrainingStats } from '@/src/domain/models/TrainingLaunch';
import type { UserStats } from '@/src/domain/usecases/GetUserStatsUseCase';
import { useTranslation } from '@/src/i18n';
import {
  ProfileScreen,
  ProfileScreenError,
  ProfileScreenLoading,
} from '@/src/presentation/screens/ProfileScreen';
import { useFocusEffect } from '@react-navigation/native';
import Head from 'expo-router/head';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Platform, Share } from 'react-native';
import { platformAlert } from '@/src/core/utils/platformAlert';

export default function UserProfileContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { userId } = useLocalSearchParams<{ userId: string }>();
  const [userEmail, setUserEmail] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [stats, setStats] = useState<UserStats | null>(null);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [posts, setPosts] = useState<PostWithDetails[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [loadingMorePosts, setLoadingMorePosts] = useState(false);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [postsPage, setPostsPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const [userBio, setUserBio] = useState<string>('');
  const [userProfile, setUserProfile] = useState<any>(null);
  const [socialCounts, setSocialCounts] = useState<SocialCounts | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [friendshipStatus, setFriendshipStatus] = useState<'none' | 'pending_sent' | 'pending_received' | 'friends'>('none');
  const [isUserPro, setIsUserPro] = useState(false);
  const [trainingStats, setTrainingStats] = useState<UserTrainingStats | null>(null);

  useFocusEffect(
    useCallback(() => {
      loadUserProfile();
    }, [userId])
  );

  const loadUserProfile = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      const { data: userData } = await supabase.rpc('search_user_by_email_by_id', {
        user_id: userId,
      });

      if (!userData || userData.length === 0) {
        setError('User not found');
        return;
      }

      const profile = userData[0];
      setUserProfile(profile);
      setUserEmail(profile.email);

      const metadata = profile.raw_user_meta_data || {};
      setUserName(metadata.full_name || profile.email.split('@')[0]);
      setUserBio(metadata.bio || '');

      const userStats = await getUserStatsUseCase.execute(userId);
      setStats(userStats);

      const userTournaments = await getUserTournamentsUseCase.execute(userId);
      await participantRepository.getByUserId(userId);
      setTournaments(userTournaments);

      // Load social data
      const counts = await getSocialCountsUseCase.execute(userId);
      setSocialCounts(counts);

      // Check Pro subscription
      const sub = await getSubscriptionUseCase.execute(userId);
      setIsUserPro(sub?.plan === 'pro');

      // Load training stats (non-blocking — may return null if user has no launches)
      try {
        const tStats = await getUserTrainingStatsUseCase.execute(userId);
        setTrainingStats(tStats);
      } catch {
        // Non-critical — silently skip if table doesn't exist yet or user has no data
      }

      // Load user posts
      setLoadingPosts(true);
      try {
        const requestingId = user?.id || userId;
        const userPosts = await getUserPostsUseCase.execute(userId, requestingId, 20, 0);
        setPosts(userPosts);
        setPostsPage(0);
        setHasMorePosts(userPosts.length === 20);
      } catch (error) {
        logger.error('Profile', 'Failed to load posts', { error });
        setPosts([]);
      } finally {
        setLoadingPosts(false);
      }

      // Check follow status if not own profile
      if (user && user.id !== userId) {
        const following = await followRepository.isFollowing(user.id, userId);
        setIsFollowing(following);

        // Check friendship status
        const areFriends = await friendshipRepository.areFriends(user.id, userId);
        if (areFriends) {
          setFriendshipStatus('friends');
        } else {
          const pendingRequest = await friendshipRepository.getPendingRequestBetween(user.id, userId);
          if (pendingRequest) {
            if (pendingRequest.requesterId === user.id) {
              setFriendshipStatus('pending_sent');
            } else {
              setFriendshipStatus('pending_received');
            }
          } else {
            setFriendshipStatus('none');
          }
        }
      }
    } catch (error) {
      logger.error('Profile', 'Failed to load user profile', { error });
      setError('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const handleTournamentPress = (tournamentId: string, isPublic?: boolean) => {
    if (isPublic) {
      router.push(`/public/${tournamentId}`);
    } else {
      router.push(`/tournament/${tournamentId}`);
    }
  };

  const handleEditProfile = () => {
    router.push('/edit-profile');
  };

  const handleFollowToggle = async () => {
    if (!user || !userId) return;

    try {
      if (isFollowing) {
        await unfollowUserUseCase.execute(user.id, userId);
        setIsFollowing(false);
        setSocialCounts(prev => prev ? { ...prev, followersCount: prev.followersCount - 1 } : null);
        platformAlert(t('common.success'), t('alerts.unfollowSuccess'));
      } else {
        await followUserUseCase.execute(user.id, userId);
        setIsFollowing(true);
        setSocialCounts(prev => prev ? { ...prev, followersCount: prev.followersCount + 1 } : null);
        platformAlert(t('common.success'), t('alerts.followSuccess'));
      }
    } catch (error) {
      logger.error('Profile', 'Failed to toggle follow', { error });
      platformAlert(t('common.error'), error instanceof Error ? error.message : t('errors.failedToLoad'));
    }
  };

  const handleFriendAction = async () => {
    if (!user || !userId) return;

    try {
      if (friendshipStatus === 'friends') {
        platformAlert(t('social.alreadyFriends'), t('alerts.alreadyFriends'));
      } else if (friendshipStatus === 'pending_sent') {
        platformAlert(t('profile.requestSent'), t('alerts.requestPending'));
      } else if (friendshipStatus === 'pending_received') {
        // Navigate to friend requests screen
        router.push('/friend-requests');
      } else {
        await sendFriendRequestUseCase.execute(user.id, userId);
        setFriendshipStatus('pending_sent');

        // Send friend request notification
        try {
          const senderName = user.user_metadata?.full_name || user.email?.split('@')[0] || '';
          await sendNotificationUseCase.execute(
            userId,
            'friend_request',
            t('notifications.friendRequest'),
            t('notifications.friendRequestBody', { name: senderName }),
            { userId: user.id }
          );
        } catch {}

        platformAlert(t('common.success'), t('social.requestSent'));
      }
    } catch (error) {
      logger.error('Profile', 'Failed to send friend request', { error });
      platformAlert(t('common.error'), error instanceof Error ? error.message : t('errors.failedToLoad'));
    }
  };

  const handleChallenge = () => {
    if (!user || !userId) return;

    const sendChallenge = async (message?: string) => {
      try {
        await sendChallengeUseCase.execute(user.id, userId, message);

        // Send challenge notification
        try {
          const senderName = user.user_metadata?.full_name || user.email?.split('@')[0] || '';
          await sendNotificationUseCase.execute(
            userId,
            'challenge_received',
            t('notifications.challengeReceived'),
            t('notifications.challengeReceivedBody', { name: senderName }),
            { userId: user.id }
          );
        } catch {}

        platformAlert(t('common.success'), t('alerts.challengeSent'));
      } catch (error) {
        logger.error('Profile', 'Failed to send challenge', { error });
        platformAlert(t('common.error'), t('errors.failedToLoad'));
      }
    };

    if (Platform.OS === 'ios') {
      Alert.prompt(
        t('social.challengeUser'),
        t('social.addMessage'),
        [
          { text: t('common.cancel'), style: 'cancel' },
          {
            text: t('social.sendChallenge'),
            onPress: (message?: string) => sendChallenge(message),
          },
        ],
        'plain-text'
      );
    } else {
      // Android doesn't support Alert.prompt, so use a simple confirmation
      platformAlert(
        t('social.challengeUser'),
        `${t('social.challenge')} ${userName}?`,
        [
          { text: t('common.cancel'), style: 'cancel' },
          {
            text: t('social.sendChallenge'),
            onPress: () => sendChallenge(),
          },
        ]
      );
    }
  };

  const handleFollowersPress = () => {
    if (userId) {
      router.push(`/followers/${userId}`);
    }
  };

  const handleFollowingPress = () => {
    if (userId) {
      router.push(`/following/${userId}`);
    }
  };

  const handleFriendsPress = () => {
    router.push('/friends');
  };

  const handleLoadMorePosts = async () => {
    if (loadingMorePosts || !hasMorePosts || !userId) return;

    try {
      setLoadingMorePosts(true);
      const nextPage = postsPage + 1;
      const requestingId = user?.id || userId;
      const data = await getUserPostsUseCase.execute(userId, requestingId, 20, nextPage * 20);

      if (data.length > 0) {
        setPosts((prev) => [...prev, ...data]);
        setPostsPage(nextPage);
        setHasMorePosts(data.length === 20);
      } else {
        setHasMorePosts(false);
      }
    } catch (error) {
      logger.error('Profile', 'Failed to load more posts', { error });
    } finally {
      setLoadingMorePosts(false);
    }
  };

  const handleLikePress = async (postId: string, isLiked: boolean) => {
    if (!user) return;

    // Optimistic update
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? {
              ...p,
              isLikedByUser: !isLiked,
              likeCount: isLiked ? p.likeCount - 1 : p.likeCount + 1,
            }
          : p
      )
    );

    try {
      if (isLiked) {
        await unlikePostUseCase.execute(postId, user.id);
      } else {
        await likePostUseCase.execute(postId, user.id);
      }
    } catch (error) {
      // Revert optimistic update on error
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId
            ? {
                ...p,
                isLikedByUser: isLiked,
                likeCount: isLiked ? p.likeCount + 1 : p.likeCount - 1,
              }
            : p
        )
      );
      logger.error('Profile', 'Failed to toggle like', { error });
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!user) return;

    platformAlert(t('feed.deletePost'), t('feed.deletePostConfirm'), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('common.delete'),
        style: 'destructive',
        onPress: async () => {
          try {
            await deletePostUseCase.execute(postId, user.id);
            setPosts((prev) => prev.filter((p) => p.id !== postId));
            platformAlert(t('common.success'), t('alerts.postDeleted'));
          } catch (error) {
            logger.error('Profile', 'Failed to delete post', { error });
            platformAlert(t('common.error'), t('feed.failedToPost'));
          }
        },
      },
    ]);
  };

  const handleUserPress = (clickedUserId: string) => {
    router.push(`/profile/${clickedUserId}`);
  };

  const handlePostTournamentPress = (tournamentId: string) => {
    router.push(`/tournament/${tournamentId}`);
  };

  const handleSharePost = async (postId: string) => {
    const url = `https://tournaly.app/post/${postId}`;
    const title = t('feed.sharePost');
    if (Platform.OS === 'web') {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ url, title }).catch(() => {});
      } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(url).catch(() => {});
        platformAlert(t('common.success'), t('feed.linkCopied'));
      }
    } else {
      await Share.share({ message: url, title }).catch(() => {});
    }
  };

  const seoDescription = useMemo(() => {
    if (!stats) return '';
    const parts: string[] = [];
    if (userBio) {
      parts.push(userBio);
    } else {
      parts.push(`${userName}'s profile on Tournaly.`);
    }
    if (stats.tournamentsPlayed > 0) {
      parts.push(
        `${stats.tournamentsPlayed} tournament${stats.tournamentsPlayed !== 1 ? 's' : ''} played, ${stats.wins} win${stats.wins !== 1 ? 's' : ''}.`
      );
    }
    return parts.join(' ');
  }, [userName, userBio, stats]);

  const seoUrl = `https://tournaly.app/profile/${userId}`;

  // Inject JSON-LD directly — avoids react-helmet-async interfering with external scripts (AdSense)
  useEffect(() => {
    if (Platform.OS !== 'web' || !stats) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'profile-jsonld';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: userName,
      url: seoUrl,
      ...(userBio && { description: userBio }),
      ...(userProfile?.raw_user_meta_data?.avatar_url && {
        image: userProfile.raw_user_meta_data.avatar_url,
      }),
    });
    document.getElementById('profile-jsonld')?.remove();
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [userName, userBio, userProfile, seoUrl, stats]);

  if (loading) {
    return <ProfileScreenLoading colors={colors} />;
  }

  if (error || !stats) {
    return <ProfileScreenError colors={colors} message={error || 'User not found'} />;
  }

  return (
    <>
      <Head>
        <title>{`${userName} — Tournaly`}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={seoUrl} />
        <meta property="og:title" content={`${userName} — Tournaly`} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:type" content="profile" />
        {userProfile?.raw_user_meta_data?.avatar_url && (
          <meta property="og:image" content={userProfile.raw_user_meta_data.avatar_url} />
        )}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${userName} — Tournaly`} />
        <meta name="twitter:description" content={seoDescription} />
      </Head>
      <Stack.Screen options={{ title: userName || t('navigation.profile') }} />
      <ProfileScreen
        userName={userName}
        userEmail={userEmail}
        userBio={userBio}
        userProfile={userProfile}
        stats={stats}
        tournaments={tournaments}
        posts={posts}
        loadingPosts={loadingPosts}
        loadingMorePosts={loadingMorePosts}
        hasMorePosts={hasMorePosts}
        isOwnProfile={userId === user?.id}
        socialCounts={socialCounts || undefined}
        isFollowing={isFollowing}
        friendshipStatus={friendshipStatus}
        handleTournamentPress={handleTournamentPress}
        handleEditProfile={handleEditProfile}
        handleFollowToggle={userId !== user?.id ? handleFollowToggle : undefined}
        handleFriendAction={userId !== user?.id ? handleFriendAction : undefined}
        handleChallenge={userId !== user?.id ? handleChallenge : undefined}
        handleFollowersPress={handleFollowersPress}
        handleFollowingPress={handleFollowingPress}
        handleFriendsPress={handleFriendsPress}
        handleUserPress={handleUserPress}
        handlePostTournamentPress={handlePostTournamentPress}
        handleDeletePost={userId === user?.id ? handleDeletePost : undefined}
        handleLikePress={handleLikePress}
        handleSharePost={handleSharePost}
        handleLoadMorePosts={handleLoadMorePosts}
        colors={colors}
        isUserPro={isUserPro}
        trainingStats={trainingStats}
      />
    </>
  );
}
